import * as React from 'react'
import BookingBlock, { BookingShape } from './BookingBlock'

interface TimelineBarProps {
  bookings: BookingShape[]
  timelineStartHour: number
  timelineEndHour: number
  currentTimeIso: string
}

const CELL_HEIGHT = 50 // pixels

const TimelineBar: React.FC<TimelineBarProps> = ({ bookings, timelineStartHour, timelineEndHour, currentTimeIso }) => {
  const totalHours = timelineEndHour + 1 - timelineStartHour
  const timelineHeight = totalHours * CELL_HEIGHT

  const getBookingStyle = (booking: BookingShape) => {
    const today = new Date(currentTimeIso).setHours(0, 0, 0, 0)
    const timelineStartDateTime = new Date(today).setHours(timelineStartHour, 0, 0, 0)
    const timelineEndDateTime = new Date(today).setHours(timelineEndHour + 1, 0, 0, 0)

    const bookingStartTime = new Date(booking.start_time).getTime()
    const bookingEndTime = new Date(booking.end_time).getTime()

    const visibleBookingStart = Math.max(bookingStartTime, timelineStartDateTime)
    const visibleBookingEnd = Math.min(bookingEndTime, timelineEndDateTime)

    if (visibleBookingEnd <= visibleBookingStart) {
      return {}
    }

    const hoursFromStart = (visibleBookingStart - timelineStartDateTime) / (3600 * 1000)
    const durationHours = (visibleBookingEnd - visibleBookingStart) / (3600 * 1000)

    let topPixels = hoursFromStart * CELL_HEIGHT
    let heightPixels = durationHours * CELL_HEIGHT

    topPixels = Math.max(0, Math.min(topPixels, timelineHeight))
    heightPixels = Math.max(0, Math.min(heightPixels, timelineHeight - topPixels))

    const isPast = new Date(booking.end_time).getTime() < new Date(currentTimeIso).getTime()
    const backgroundColor = isPast ? '#b6bfbf' : '#4C7D75'
    const borderColor = isPast ? '#a9b2b2' : '#3a5e58'

    return {
      top: `${topPixels.toFixed(2)}px`,
      height: `${heightPixels.toFixed(2)}px`,
      backgroundColor,
      borderColor,
    }
  }

  const getCurrentTimeIndicatorStyle = () => {
    const today = new Date(currentTimeIso).setHours(0, 0, 0, 0)
    const timelineStartDateTime = new Date(today).setHours(timelineStartHour, 0, 0, 0)
    const currentTime = new Date(currentTimeIso).getTime()

    const hoursFromStart = (currentTime - timelineStartDateTime) / (3600 * 1000)
    const topPixels = hoursFromStart * CELL_HEIGHT

    if (topPixels < 0 || topPixels > timelineHeight) {
      return {}
    }
    return { top: `${topPixels.toFixed(2)}px` }
  }

  const hourMarkers = []
  for (let hour = timelineStartHour; hour <= timelineEndHour; hour++) {
    hourMarkers.push(
      <div className="hour-marker" key={`hour-${hour}`} style={{ top: `${(hour - timelineStartHour) * CELL_HEIGHT}px` }}>
        <div className="hour-label">{`${hour}:00`}</div>
      </div>
    )
  }

  return (
    <div className="timeline-column" style={{ height: `${timelineHeight}px` }}>
      {hourMarkers}
      {Object.keys(getCurrentTimeIndicatorStyle()).length > 0 && (
        <div className="current-time-indicator" style={getCurrentTimeIndicatorStyle()} />
      )}
      {bookings.map(booking => {
        const style = getBookingStyle(booking)
        if (Object.keys(style).length === 0) return null
        return <BookingBlock key={booking.id} booking={booking} style={style} />
      })}
    </div>
  )
}

export default TimelineBar
